import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Clapperboard,
  Film,
  FolderPlus,
  LayoutGrid,
  Plus,
  RefreshCw,
  Search,
  Save,
  ShieldCheck,
  LogOut,
  Trash2,
  Tv,
  Upload,
  LoaderCircle,
  FileVideo2,
  Users,
  Settings,
  Tags,
  X,
} from "lucide-react";
import { authApi, contentApi, mediaUrl } from "@services/contentApi";
import "@styles/admin.css";

const emptyForm = {
  title: "",
  originalTitle: "",
  type: "movie",
  status: "draft",
  description: "",
  category: "",
  genres: [],
  countries: "",
  ageRating: "16+",
  rating: "0",
  releaseDate: "",
  duration: "0",
  introStart: "0",
  introEnd: "0",
  creditsStart: "0",
  creditsEnd: "0",
  isFeatured: false,
};

function appendContentForm(form, poster, backdrop, logo) {
  const data = new FormData();
  Object.entries(form).forEach(([key, value]) => data.append(key, value));
  if (poster) data.append("poster", poster);
  if (backdrop) data.append("backdrop", backdrop);
  if (logo) data.append("logo", logo);
  return data;
}

function getErrorMessage(error) {
  return error.response?.data?.message || error.message || "Ошибка запроса";
}

function getSourceLabel(source) {
  const sizeMb = source.size ? `, ${(source.size / 1024 / 1024).toFixed(1)} МБ` : "";
  return `${source.quality}${sizeMb}`;
}

export default function AdminPage() {
  const [contents, setContents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [poster, setPoster] = useState(null);
  const [backdrop, setBackdrop] = useState(null);
  const [logo, setLogo] = useState(null);
  const [categoryTitle, setCategoryTitle] = useState("");
  const [genreTitle, setGenreTitle] = useState("");
  const [seasonTitle, setSeasonTitle] = useState("");
  const [episodeForm, setEpisodeForm] = useState({ seasonId: "", title: "", episodeNumber: "", duration: "" });
  const [movieVideo, setMovieVideo] = useState({ file: null });
  const [episodeVideo, setEpisodeVideo] = useState({ episodeId: "", file: null });
  const [isBusy, setIsBusy] = useState(false);
  const [uploadState, setUploadState] = useState({ active: false, progress: 0, phase: "idle", fileName: "" });
  const [notice, setNotice] = useState({ type: "info", text: "" });
  const [authLogs, setAuthLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("content");
  const [editorOpen, setEditorOpen] = useState(false);
  const [adminFilters, setAdminFilters] = useState({ search: "", type: "all", status: "all" });

  const selected = useMemo(() => contents.find((item) => item._id === selectedId), [contents, selectedId]);
  const filteredContents = useMemo(() => contents.filter((item) => {
    const searchMatch = `${item.title} ${item.originalTitle || ""}`.toLowerCase().includes(adminFilters.search.toLowerCase());
    return searchMatch && (adminFilters.type === "all" || item.type === adminFilters.type) && (adminFilters.status === "all" || item.status === adminFilters.status);
  }), [adminFilters, contents]);

  const episodes = useMemo(() => {
    if (!selected?.seasons?.length) return [];

    return selected.seasons.flatMap((season) =>
      (season.episodes || []).map((episode) => ({
        ...episode,
        seasonId: season._id,
        seasonNumber: season.seasonNumber,
      }))
    );
  }, [selected]);

  const selectedEpisode = useMemo(
    () => episodes.find((episode) => episode._id === episodeVideo.episodeId),
    [episodes, episodeVideo.episodeId]
  );

  const isMovie = form.type === "movie";
  const cardSaved = Boolean(selectedId);

  const showNotice = (type, text) => setNotice({ type, text });
  const beginUpload = (file) => setUploadState({ active: true, progress: 0, phase: "uploading", fileName: file.name });
  const trackUpload = (event) => {
    const progress = event.total ? Math.min(100, Math.round((event.loaded * 100) / event.total)) : 0;
    setUploadState((current) => ({ ...current, progress, phase: progress >= 100 ? "processing" : "uploading" }));
  };
  const finishUpload = (success) => setUploadState((current) => ({ ...current, active: false, progress: success ? 100 : current.progress, phase: success ? "done" : "error" }));
  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const loadData = useCallback(async (nextSelectedId) => {
    const [contentList, categoryList, genreList] = await Promise.all([
      contentApi.listContents(),
      contentApi.listCategories(),
      contentApi.listGenres(),
    ]);
    setContents(contentList);
    setCategories(categoryList);
    setGenres(genreList);
    setSelectedId((current) => nextSelectedId || current || "");
  }, []);

  const refreshContentState = async (updatedContent) => {
    await loadData(updatedContent?._id || selectedId);
  };

  useEffect(() => {
    loadData().catch((error) => showNotice("error", getErrorMessage(error)));
    authApi.logs(100).then(setAuthLogs).catch((error) => showNotice("error", getErrorMessage(error)));
    authApi.users(300).then(setUsers).catch((error) => showNotice("error", getErrorMessage(error)));
  }, [loadData]);

  const logout = async () => {
    await authApi.logout().catch(() => {});
    window.location.assign("/login");
  };

  useEffect(() => {
    if (!selected) return;

    setForm({
      title: selected.title || "",
      originalTitle: selected.originalTitle || "",
      type: selected.type || "movie",
      status: selected.status || "draft",
      description: selected.description || "",
      category: selected.category?._id || selected.category || "",
      genres: selected.genres || [],
      countries: selected.countries?.join(", ") || "",
      ageRating: selected.ageRating || "",
      rating: String(selected.rating || 0),
      releaseDate: selected.releaseDate ? selected.releaseDate.slice(0, 10) : "",
      duration: String(selected.duration || 0),
      introStart: String(selected.intro?.start || 0),
      introEnd: String(selected.intro?.end || 0),
      creditsStart: String(selected.credits?.start || 0),
      creditsEnd: String(selected.credits?.end || 0),
      isFeatured: Boolean(selected.isFeatured),
    });
    setPoster(null);
    setBackdrop(null);
    setLogo(null);
    setEpisodeVideo({ episodeId: "", file: null });
  }, [selected]);

  const createNew = (type = "movie") => {
    setSelectedId("");
    setForm({ ...emptyForm, type });
    setEditorOpen(true);
    setPoster(null);
    setBackdrop(null);
    setLogo(null);
    setSeasonTitle("");
    setEpisodeForm({ seasonId: "", title: "", episodeNumber: "", duration: "" });
    setMovieVideo({ file: null });
    setEpisodeVideo({ episodeId: "", file: null });
    showNotice("info", "Заполните карточку и сохраните ее перед загрузкой видео.");
  };

  const editContent = (id) => {
    setSelectedId(id);
    setEditorOpen(true);
  };

  const removeContentById = async (id, event) => {
    event?.stopPropagation();
    if (!window.confirm("Удалить эту карточку контента?")) return;
    setIsBusy(true);
    try {
      await contentApi.deleteContent(id);
      if (selectedId === id) setSelectedId("");
      await loadData("");
      showNotice("success", "Карточка удалена.");
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally { setIsBusy(false); }
  };

  const submitContent = async (event) => {
    event.preventDefault();
    setIsBusy(true);
    showNotice("info", "");

    try {
      const data = appendContentForm(form, poster, backdrop, logo);
      const saved = selectedId
        ? await contentApi.updateContent(selectedId, data)
        : await contentApi.createContent(data);

      await refreshContentState(saved);
      showNotice("success", "Карточка сохранена. Теперь можно загружать видео.");
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  const removeContent = async () => {
    if (!selectedId) return;

    setIsBusy(true);
    try {
      await contentApi.deleteContent(selectedId);
      setSelectedId("");
      await loadData("");
      showNotice("success", "Карточка удалена.");
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  const createCategory = async () => {
    const title = categoryTitle.trim();
    if (!title) {
      showNotice("error", "Введите название категории.");
      return;
    }

    setIsBusy(true);
    try {
      await contentApi.createCategory({ title });
      setCategoryTitle("");
      await loadData(selectedId);
      showNotice("success", "Категория создана.");
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  const createGenre = async () => {
    const title = genreTitle.trim();
    if (!title) return showNotice("error", "Введите название жанра.");
    setIsBusy(true);
    try {
      await contentApi.createGenre({ title });
      setGenreTitle("");
      await loadData(selectedId);
      showNotice("success", "Жанр добавлен и доступен в карточке контента.");
    } catch (error) { showNotice("error", getErrorMessage(error)); }
    finally { setIsBusy(false); }
  };

  const deleteSetting = async (kind, item) => {
    if (!window.confirm(`Удалить «${item.title}»?`)) return;
    setIsBusy(true);
    try {
      if (kind === "genre") await contentApi.deleteGenre(item._id);
      else await contentApi.deleteCategory(item._id);
      await loadData(selectedId);
      showNotice("success", kind === "genre" ? "Жанр удалён из настроек и карточек." : "Категория удалена.");
    } catch (error) { showNotice("error", getErrorMessage(error)); }
    finally { setIsBusy(false); }
  };

  const toggleGenre = (title) => setForm((current) => ({
    ...current,
    genres: current.genres.includes(title) ? current.genres.filter((item) => item !== title) : [...current.genres, title],
  }));

  const toggleForYou = async (item) => {
    setIsBusy(true);
    try {
      const updated = await contentApi.setForYou(item._id, !item.isForYou);
      setContents((current) => current.map((content) => content._id === updated._id ? updated : content));
      showNotice("success", updated.isForYou ? `«${updated.title}» добавлен в подборку.` : `«${updated.title}» удалён из подборки.`);
    } catch (error) { showNotice("error", getErrorMessage(error)); }
    finally { setIsBusy(false); }
  };

  const uploadMovieVideo = async () => {
    if (!movieVideo.file) {
      showNotice("error", "Выберите видеофайл для фильма.");
      return;
    }
    if (!selectedId && !form.title.trim()) {
      showNotice("error", "Введите название фильма — карточка сохранится автоматически.");
      return;
    }

    setIsBusy(true);
    beginUpload(movieVideo.file);
    showNotice("info", "Видео загружается и обрабатывается. Не закрывайте страницу до завершения.");
    try {
      let contentId = selectedId;

      if (!contentId) {
        const saved = await contentApi.createContent(
          appendContentForm({ ...form, type: "movie" }, poster, backdrop, logo)
        );
        contentId = saved._id;
        setSelectedId(contentId);
      }

      const data = new FormData();
      data.append("video", movieVideo.file);
      const updated = await contentApi.addMovieVideo(contentId, data, trackUpload);
      setMovieVideo({ file: null });
      await refreshContentState(updated);
      showNotice("success", "Видео загружено. Все доступные разрешения созданы.");
      finishUpload(true);
    } catch (error) {
      showNotice("error", getErrorMessage(error));
      finishUpload(false);
    } finally {
      setIsBusy(false);
    }
  };

  const addSeason = async () => {
    if (!selectedId) {
      showNotice("error", "Сначала сохраните карточку сериала.");
      return;
    }

    setIsBusy(true);
    try {
      const updated = await contentApi.addSeason(selectedId, { title: seasonTitle });
      setSeasonTitle("");
      await refreshContentState(updated);
      showNotice("success", "Сезон добавлен.");
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  const addEpisode = async () => {
    if (!selectedId) {
      showNotice("error", "Сначала сохраните карточку сериала.");
      return;
    }
    if (!episodeForm.seasonId) {
      showNotice("error", "Выберите сезон для серии.");
      return;
    }
    if (!episodeForm.title.trim()) {
      showNotice("error", "Введите название серии.");
      return;
    }

    setIsBusy(true);
    try {
      const updated = await contentApi.addEpisode(selectedId, episodeForm.seasonId, episodeForm);
      setEpisodeForm({ seasonId: "", title: "", episodeNumber: "", duration: "" });
      await refreshContentState(updated);
      showNotice("success", "Серия добавлена.");
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  const uploadEpisodeVideo = async () => {
    if (!selectedId) {
      showNotice("error", "Сначала сохраните карточку сериала.");
      return;
    }
    if (!selectedEpisode) {
      showNotice("error", "Выберите серию для загрузки видео.");
      return;
    }
    if (!episodeVideo.file) {
      showNotice("error", "Выберите видеофайл для серии.");
      return;
    }

    setIsBusy(true);
    beginUpload(episodeVideo.file);
    showNotice("info", "Видео серии загружается. После загрузки сервер подготовит качества.");
    try {
      const data = new FormData();
      data.append("video", episodeVideo.file);
      const updated = await contentApi.addEpisodeVideo(
        selectedId,
        selectedEpisode.seasonId,
        selectedEpisode._id,
        data,
        trackUpload
      );
      setEpisodeVideo({ episodeId: "", file: null });
      await refreshContentState(updated);
      showNotice("success", "Видео серии загружено.");
      finishUpload(true);
    } catch (error) {
      showNotice("error", getErrorMessage(error));
      finishUpload(false);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <main className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <Film size={22} />
          <span>Smotri Admin</span>
        </div>

        <nav className="admin-nav">
          <button className={activeTab === "content" ? "active" : ""} onClick={() => { setActiveTab("content"); setEditorOpen(false); }}><LayoutGrid size={18} /> Контент <span>{contents.length}</span></button>
          <button className={activeTab === "users" ? "active" : ""} onClick={() => setActiveTab("users")}><Users size={18} /> Пользователи <span>{users.length}</span></button>
          <button className={activeTab === "logs" ? "active" : ""} onClick={() => setActiveTab("logs")}><ShieldCheck size={18} /> Авторизации <span>{authLogs.length}</span></button>
          <button className={activeTab === "settings" ? "active" : ""} onClick={() => { setActiveTab("settings"); setEditorOpen(false); }}><Settings size={18} /> Настройки</button>
        </nav>
        <div className="admin-sidebar-foot"><Link to="/">На сайт</Link><button onClick={logout}><LogOut size={16} /> Выйти</button></div>
      </aside>

      <section className="admin-workspace">
        <header className="admin-topbar">
          <div>
            <h1>{activeTab === "users" ? "Пользователи" : activeTab === "logs" ? "История входов" : activeTab === "settings" ? "Настройки каталога" : editorOpen ? (selectedId ? "Редактирование" : "Новый контент") : "Управление контентом"}</h1>
            <p>{activeTab === "content" ? "Фильмы и сериалы в каталоге Smotri." : activeTab === "users" ? "Аккаунты, Telegram и состояние подписок." : activeTab === "settings" ? "Категории разделов и жанры контента." : "Последние события безопасности аккаунтов."}</p>
          </div>
          <div className="admin-actions">
            {activeTab === "content" && editorOpen && selectedId && (
              <Link to={`/watch/${selectedId}`} className="admin-link">
                Открыть плеер
              </Link>
            )}
            <button type="button" onClick={() => loadData(selectedId)} aria-label="Обновить">
              <RefreshCw size={16} />
            </button>
            {activeTab === "content" && !editorOpen && <button className="admin-add-top" type="button" onClick={() => createNew("movie")}><Plus size={16} /> Добавить фильм</button>}
            {activeTab === "content" && !editorOpen && <button className="admin-add-top secondary" type="button" onClick={() => createNew("series")}><Plus size={16} /> Добавить сериал</button>}
            {activeTab === "content" && editorOpen && <button className="admin-add-top secondary" type="button" onClick={() => setEditorOpen(false)}>К списку</button>}
          </div>
        </header>

        {notice.text && (
          <div className={`admin-message ${notice.type}`}>
            {notice.type === "error" ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
            <span>{notice.text}</span>
          </div>
        )}

        {activeTab === "content" && !editorOpen && (
          <section className="admin-catalog-view">
            <div className="admin-catalog-filters">
              <label><Search size={18} /><input value={adminFilters.search} onChange={(event) => setAdminFilters((current) => ({ ...current, search: event.target.value }))} placeholder="Поиск по названию" /></label>
              <select value={adminFilters.type} onChange={(event) => setAdminFilters((current) => ({ ...current, type: event.target.value }))}><option value="all">Все типы</option><option value="movie">Фильмы</option><option value="series">Сериалы</option></select>
              <select value={adminFilters.status} onChange={(event) => setAdminFilters((current) => ({ ...current, status: event.target.value }))}><option value="all">Все статусы</option><option value="published">Опубликовано</option><option value="draft">Черновики</option><option value="archived">Архив</option></select>
            </div>
            <div className="admin-content-grid">
              {filteredContents.map((item) => (
                <article className="admin-content-card" key={item._id} onClick={() => editContent(item._id)}>
                  <div className="admin-content-poster">
                    {item.posterUrl ? <img src={mediaUrl(item.posterUrl)} alt={item.title} /> : <Film size={34} />}
                    <span className={`admin-card-status ${item.status}`}>{item.status === "published" ? "Опубликован" : item.status === "draft" ? "Черновик" : "Архив"}</span>
                    <div className="admin-card-hover"><button onClick={(event) => removeContentById(item._id, event)} disabled={isBusy}><Trash2 size={18} /> Удалить</button></div>
                  </div>
                  <div className="admin-content-info"><h3>{item.title}</h3><p>{item.type === "series" ? "Сериал" : "Фильм"} · {item.releaseDate ? new Date(item.releaseDate).getFullYear() : "год не указан"}</p><div><span>{item.rating ? `★ ${Number(item.rating).toFixed(1)}` : "Нет рейтинга"}</span><small>Редактировать →</small></div></div>
                </article>
              ))}
              {!filteredContents.length && <div className="admin-catalog-empty"><Search size={27} /><h3>Контент не найден</h3><p>Измените поисковый запрос или фильтры.</p></div>}
            </div>
          </section>
        )}

        {activeTab === "content" && editorOpen && <>
        <div className="admin-steps">
          <div className={cardSaved ? "admin-step done" : "admin-step active"}>
            <span>1</span>
            <div>
              <strong>Карточка</strong>
              <small>{cardSaved ? "Сохранена" : "Заполните и сохраните"}</small>
            </div>
          </div>
          <div className={cardSaved ? "admin-step active" : "admin-step"}>
            <span>2</span>
            <div>
              <strong>{isMovie ? "Видео фильма" : "Сезоны и серии"}</strong>
              <small>{isMovie ? "Одно или несколько качеств" : "Сезон -> серия -> видео"}</small>
            </div>
          </div>
        </div>

        <form className="admin-grid" onSubmit={submitContent}>
          <div className="admin-panel wide">
            <div className="admin-section-title">
              <h2>Карточка</h2>
              <span>{selectedId ? "Редактирование" : "Новая"}</span>
            </div>

            <div className="admin-form-grid">
              <label>
                Название
                <input value={form.title} onChange={(event) => setField("title", event.target.value)} required />
              </label>
              <label>
                Оригинальное название
                <input value={form.originalTitle} onChange={(event) => setField("originalTitle", event.target.value)} />
              </label>
              <label>
                Тип
                <select value={form.type} onChange={(event) => setField("type", event.target.value)}>
                  <option value="movie">Фильм</option>
                  <option value="series">Сериал</option>
                </select>
              </label>
              <label>
                Статус
                <select value={form.status} onChange={(event) => setField("status", event.target.value)}>
                  <option value="draft">Черновик</option>
                  <option value="published">Опубликован</option>
                  <option value="archived">Архив</option>
                </select>
              </label>
              <label>
                Категория
                <select value={form.category} onChange={(event) => setField("category", event.target.value)}>
                  <option value="">Без категории</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Рейтинг
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={form.rating}
                  onChange={(event) => setField("rating", event.target.value)}
                />
              </label>
              <label>
                Дата выхода
                <input type="date" value={form.releaseDate} onChange={(event) => setField("releaseDate", event.target.value)} />
              </label>
              <label>
                Возраст
                <input value={form.ageRating} onChange={(event) => setField("ageRating", event.target.value)} />
              </label>
              <label className="admin-genre-field">
                Жанры
                <details className="admin-multi-select">
                  <summary><span>{form.genres.length ? form.genres.join(", ") : "Выберите жанры"}</span><ChevronDown size={17} /></summary>
                  <div>{genres.length ? genres.map((genre) => <button type="button" className={form.genres.includes(genre.title) ? "selected" : ""} onClick={() => toggleGenre(genre.title)} key={genre._id}><i>{form.genres.includes(genre.title) && <CheckCircle2 size={13} />}</i>{genre.title}</button>) : <p>Сначала добавьте жанры в настройках</p>}</div>
                </details>
              </label>
              <label>
                Страны
                <input value={form.countries} onChange={(event) => setField("countries", event.target.value)} placeholder="США, Франция" />
              </label>
              <label>
                Длительность, сек
                <input type="number" min="0" value={form.duration} onChange={(event) => setField("duration", event.target.value)} />
              </label>
              <label className="admin-checkbox">
                <input type="checkbox" checked={form.isFeatured} onChange={(event) => setField("isFeatured", event.target.checked)} />
                В подборку
              </label>
              <label className="full">
                Описание
                <textarea rows="5" value={form.description} onChange={(event) => setField("description", event.target.value)} />
              </label>
            </div>
          </div>

          <div className="admin-panel">
            <div className="admin-section-title">
              <h2>Баннеры</h2>
            </div>
            <p className="admin-help">Для блока «Скоро на Smotri» укажите будущую дату выхода, загрузите баннер и логотип, затем опубликуйте карточку.</p>
            <div className="admin-form-grid compact">
              <label>
                Постер
                <input type="file" accept="image/*" onChange={(event) => setPoster(event.target.files?.[0] || null)} />
              </label>
              <label>
                Баннер (фон)
                <input type="file" accept="image/*" onChange={(event) => setBackdrop(event.target.files?.[0] || null)} />
              </label>
              <label>
                Логотип названия
                <input type="file" accept="image/*" onChange={(event) => setLogo(event.target.files?.[0] || null)} />
              </label>
              <label>
                Заставка от, сек
                <input type="number" min="0" value={form.introStart} onChange={(event) => setField("introStart", event.target.value)} />
              </label>
              <label>
                Заставка до, сек
                <input type="number" min="0" value={form.introEnd} onChange={(event) => setField("introEnd", event.target.value)} />
              </label>
              <label>
                Концовка от, сек
                <input type="number" min="0" value={form.creditsStart} onChange={(event) => setField("creditsStart", event.target.value)} />
              </label>
              <label>
                Концовка до, сек
                <input type="number" min="0" value={form.creditsEnd} onChange={(event) => setField("creditsEnd", event.target.value)} />
              </label>
            </div>

            {selected?.posterUrl ? (
              <>
                <img className="admin-poster-preview" src={mediaUrl(selected.posterUrl)} alt={selected.title} />
                {selected?.backdropUrl && (
                  <div className="admin-media-preview-block">
                    <strong>Фон главной</strong>
                    <img className="admin-backdrop-preview" src={mediaUrl(selected.backdropUrl)} alt={`${selected.title} backdrop`} />
                  </div>
                )}
                {selected?.logoUrl && (
                  <div className="admin-media-preview-block">
                    <strong>Логотип названия</strong>
                    <div className="admin-logo-preview">
                      <img src={mediaUrl(selected.logoUrl)} alt={`${selected.title} logo`} />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="admin-preview-empty">Постер появится после сохранения</div>
            )}
          </div>
          <div className="admin-submit-row">
            <button className="admin-primary" type="submit" disabled={isBusy}>
              <Save size={18} />
              Сохранить карточку
            </button>
            {selectedId && (
              <button className="admin-danger" type="button" onClick={removeContent} disabled={isBusy}>
                <Trash2 size={18} />
                Удалить
              </button>
            )}
          </div>
        </form>

        <section className="admin-grid">
          {isMovie ? (
            <div className="admin-panel">
              <div className="admin-section-title">
                <h2>Видео фильма</h2>
                <Film size={18} />
              </div>
              <p className="admin-help">Загрузите видео в максимальном качестве: сервер автоматически создаст все доступные версии от 2160p до 360p.</p>
              <div className="inline-form">
                <input type="file" accept="video/*" onChange={(event) => setMovieVideo((current) => ({ ...current, file: event.target.files?.[0] || null }))} />
                <button type="button" onClick={uploadMovieVideo} disabled={isBusy}>
                  <Upload size={16} />
                  Загрузить
                </button>
              </div>
              {movieVideo.file && !uploadState.active && <div className="admin-file-ready"><FileVideo2 size={18} /><span><strong>{movieVideo.file.name}</strong><small>{(movieVideo.file.size / 1024 / 1024).toFixed(1)} МБ · готово к загрузке</small></span></div>}
              {(uploadState.active || uploadState.phase === "done" || uploadState.phase === "error") && <UploadStatus state={uploadState} />}
              <div className="admin-video-list">
                {selected?.videoSources?.length ? (
                  selected.videoSources.map((source) => <span key={source._id}>{getSourceLabel(source)}</span>)
                ) : (
                  <span>Видео еще не загружено</span>
                )}
              </div>
            </div>
          ) : (
            <div className="admin-panel wide">
              <div className="admin-section-title">
                <h2>Сериал</h2>
                <Tv size={18} />
              </div>

              <div className="admin-stack">
                <div>
                  <h3>1. Добавьте сезон</h3>
                  <div className="inline-form">
                    <input value={seasonTitle} onChange={(event) => setSeasonTitle(event.target.value)} placeholder="Название сезона, можно пустым" />
                    <button type="button" onClick={addSeason} disabled={isBusy}>
                      Добавить сезон
                    </button>
                  </div>
                </div>

                <div>
                  <h3>2. Добавьте серию в сезон</h3>
                  <div className="inline-form">
                    <select value={episodeForm.seasonId} onChange={(event) => setEpisodeForm((current) => ({ ...current, seasonId: event.target.value }))}>
                      <option value="">Выберите сезон</option>
                      {selected?.seasons?.map((season) => (
                        <option key={season._id} value={season._id}>
                          Сезон {season.seasonNumber}: {season.title || "Без названия"}
                        </option>
                      ))}
                    </select>
                    <input value={episodeForm.title} onChange={(event) => setEpisodeForm((current) => ({ ...current, title: event.target.value }))} placeholder="Название серии" />
                    <input type="number" value={episodeForm.episodeNumber} onChange={(event) => setEpisodeForm((current) => ({ ...current, episodeNumber: event.target.value }))} placeholder="N" />
                    <input type="number" value={episodeForm.duration} onChange={(event) => setEpisodeForm((current) => ({ ...current, duration: event.target.value }))} placeholder="Сек" />
                    <button type="button" onClick={addEpisode} disabled={isBusy}>
                      Добавить серию
                    </button>
                  </div>
                </div>

                <div>
                  <h3>3. Загрузите видео в серию</h3>
                  <div className="inline-form">
                    <select value={episodeVideo.episodeId} onChange={(event) => setEpisodeVideo((current) => ({ ...current, episodeId: event.target.value }))}>
                      <option value="">Выберите серию</option>
                      {episodes.map((episode) => (
                        <option key={episode._id} value={episode._id}>
                          S{episode.seasonNumber}E{episode.episodeNumber} {episode.title}
                        </option>
                      ))}
                    </select>
                    <input type="file" accept="video/*" onChange={(event) => setEpisodeVideo((current) => ({ ...current, file: event.target.files?.[0] || null }))} />
                    <button type="button" onClick={uploadEpisodeVideo} disabled={isBusy}>
                      <Upload size={16} />
                      Загрузить
                    </button>
                  </div>
                  {episodeVideo.file && !uploadState.active && <div className="admin-file-ready"><FileVideo2 size={18} /><span><strong>{episodeVideo.file.name}</strong><small>{(episodeVideo.file.size / 1024 / 1024).toFixed(1)} МБ · готово к загрузке</small></span></div>}
                  {(uploadState.active || uploadState.phase === "done" || uploadState.phase === "error") && <UploadStatus state={uploadState} />}
                </div>
              </div>

              <div className="admin-seasons">
                {selected?.seasons?.length ? (
                  selected.seasons.map((season) => (
                    <article className="admin-season-card" key={season._id}>
                      <div>
                        <Clapperboard size={18} />
                        <strong>Сезон {season.seasonNumber}</strong>
                        <span>{season.title || "Без названия"}</span>
                      </div>
                      {(season.episodes || []).length === 0 ? (
                        <p>Серий пока нет.</p>
                      ) : (
                        <ul>
                          {season.episodes.map((episode) => (
                            <li key={episode._id}>
                              <span>E{episode.episodeNumber} {episode.title}</span>
                              <small>{episode.videoSources?.length ? `${episode.videoSources.length} видео` : "без видео"}</small>
                            </li>
                          ))}
                        </ul>
                      )}
                    </article>
                  ))
                ) : (
                  <div className="admin-preview-empty">После сохранения карточки добавьте первый сезон</div>
                )}
              </div>
            </div>
          )}
        </section>

        </>}

        {activeTab === "settings" && <section className="admin-settings-grid">
          <div className="admin-panel admin-setting-panel">
            <div className="admin-section-title"><div><h2>Категории</h2><p>Разделы каталога: фильмы, мультфильмы, аниме и другие.</p></div><FolderPlus size={20} /></div>
            <div className="inline-form"><input value={categoryTitle} onChange={(event) => setCategoryTitle(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") createCategory(); }} placeholder="Новая категория"/><button type="button" onClick={createCategory} disabled={isBusy}><Plus size={16}/>Добавить</button></div>
            <div className="admin-setting-list">{categories.map((category) => <div key={category._id}><span><strong>{category.title}</strong><small>/{category.slug}</small></span><button onClick={() => deleteSetting("category", category)} aria-label={`Удалить ${category.title}`}><X size={16}/></button></div>)}{!categories.length && <p>Категорий пока нет.</p>}</div>
          </div>
          <div className="admin-panel admin-setting-panel">
            <div className="admin-section-title"><div><h2>Жанры</h2><p>Используются в фильтрах и подборках на главной.</p></div><Tags size={20} /></div>
            <div className="inline-form"><input value={genreTitle} onChange={(event) => setGenreTitle(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") createGenre(); }} placeholder="Например, Комедия"/><button type="button" onClick={createGenre} disabled={isBusy}><Plus size={16}/>Добавить</button></div>
            <div className="admin-setting-list">{genres.map((genre) => <div key={genre._id}><span><strong>{genre.title}</strong><small>/{genre.slug}</small></span><button onClick={() => deleteSetting("genre", genre)} aria-label={`Удалить ${genre.title}`}><X size={16}/></button></div>)}{!genres.length && <p>Добавьте первый жанр.</p>}</div>
          </div>
          <div className="admin-panel admin-setting-panel admin-for-you-setting">
            <div className="admin-section-title"><div><h2>Специально для тебя</h2><p>Выберите фильмы и сериалы, которые появятся в персональной подборке на главной.</p></div><Film size={20}/></div>
            <div className="admin-curation-list">{contents.filter((item) => item.status === "published").map((item) => <button type="button" className={item.isForYou ? "selected" : ""} onClick={() => toggleForYou(item)} disabled={isBusy} key={item._id}>{item.posterUrl ? <img src={mediaUrl(item.posterUrl)} alt=""/> : <span className="admin-curation-placeholder"><Film size={18}/></span>}<span><strong>{item.title}</strong><small>{item.type === "series" ? "Сериал" : "Фильм"}{item.genres?.length ? ` · ${item.genres.join(", ")}` : ""}</small></span><i>{item.isForYou ? <CheckCircle2 size={20}/> : <Plus size={18}/>}</i></button>)}{!contents.some((item) => item.status === "published") && <p>Сначала опубликуйте фильм или сериал.</p>}</div>
          </div>
        </section>}

        {activeTab === "users" && <UsersPanel users={users} onSubscriptionUpdated={(updated) => setUsers((current) => current.map((user) => user.id === updated.id ? updated : user))} showNotice={showNotice} />}

        {activeTab === "logs" && <section className="admin-panel auth-log-panel">
          <div className="admin-section-title">
            <h2>Журнал авторизации</h2>
            <span>{authLogs.length} последних событий</span>
          </div>
          <div className="auth-log-scroll">
            <table className="auth-log-table">
              <thead>
                <tr><th>Дата</th><th>Пользователь</th><th>Событие</th><th>IP</th><th>Браузер и язык</th><th>Результат</th></tr>
              </thead>
              <tbody>
                {authLogs.map((log) => (
                  <tr key={log._id}>
                    <td>{new Date(log.createdAt).toLocaleString("ru-RU")}</td>
                    <td>{log.user ? `${log.user.firstName} ${log.user.lastName || ""}${log.user.username ? ` (@${log.user.username})` : ""}` : log.telegramId || "Не определён"}</td>
                    <td>{log.event}</td>
                    <td title={log.forwardedFor || ""}>{log.ip || "—"}</td>
                    <td title={log.userAgent || ""}>{log.userAgent || "—"}<small>{log.language || ""}</small></td>
                    <td className={log.success ? "log-success" : "log-failed"}>{log.success ? "Успешно" : log.reason || "Ошибка"}</td>
                  </tr>
                ))}
                {!authLogs.length && <tr><td colSpan="6">Событий пока нет</td></tr>}
              </tbody>
            </table>
          </div>
        </section>}
      </section>
    </main>
  );
}

function UploadStatus({ state }) {
  const processing = state.phase === "processing";
  const title = state.phase === "done" ? "Видео готово" : state.phase === "error" ? "Загрузка прервана" : processing ? "Файл загружен — создаём качества" : `Загружаем ${state.progress}%`;
  return <div className={`admin-upload-status ${state.phase}`}>
    <div className="admin-upload-icon">{state.active ? <LoaderCircle size={20} /> : state.phase === "done" ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}</div>
    <div className="admin-upload-copy"><strong>{title}</strong><small>{state.fileName}{processing ? " · можно подождать, страница обновится автоматически" : ""}</small><div className="admin-upload-track"><i style={{ width: `${state.progress}%` }} /></div></div>
  </div>;
}

function countryFlag(code) {
  if (!/^[A-Z]{2}$/.test(code || "")) return "🌐";
  return String.fromCodePoint(...code.split("").map((char) => 127397 + char.charCodeAt()));
}

function shortAgent(value) {
  if (!value) return "Не определён";
  const browser = value.match(/(Firefox|Edg|Chrome|Safari)\/([\d.]+)/);
  const os = value.match(/(Windows NT [\d.]+|Android [\d.]+|iPhone OS [\d_]+|Mac OS X [\d_]+|Linux)/);
  return [browser?.[1], os?.[1]?.replaceAll("_", ".")].filter(Boolean).join(" · ") || value.slice(0, 60);
}

function UsersPanel({ users, onSubscriptionUpdated, showNotice }) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState("");
  const visible = users.filter((user) => `${user.firstName} ${user.lastName || ""} ${user.login || ""} ${user.username || ""} ${user.telegramId}`.toLowerCase().includes(search.toLowerCase()));

  const changeSubscription = async (user, active) => {
    try {
      const expiresAt = active ? new Date(Date.now() + 30 * 86400000).toISOString() : null;
      const result = await authApi.updateSubscription(user.id, { active, plan: active ? "Smotri Plus" : "free", expiresAt });
      onSubscriptionUpdated(result.user);
      showNotice("success", active ? "Подписка активирована на 30 дней." : "Подписка отключена.");
    } catch (error) { showNotice("error", getErrorMessage(error)); }
  };

  return <section className="admin-users-view">
    <label className="admin-users-search"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Имя, логин, Telegram ID" /></label>
    <div className="admin-user-list">{visible.map((user) => {
      const active = Boolean(user.subscription?.active && (!user.subscription.expiresAt || new Date(user.subscription.expiresAt) > new Date()));
      const country = user.lastLogin?.country || user.registration?.country || "";
      return <article className={`admin-user-card ${expanded === user.id ? "is-expanded" : ""}`} key={user.id}>
        <button className="admin-user-summary" onClick={() => setExpanded((id) => id === user.id ? "" : user.id)}>
          <span className="admin-user-avatar">{user.photoUrl ? <img src={user.photoUrl} alt="" /> : (user.firstName?.[0] || "U")}</span>
          <span className="admin-user-name"><strong>{user.firstName} {user.lastName || ""}</strong><small>{user.login || "без логина"} · {user.username ? `@${user.username}` : `TG ${user.telegramId}`}</small></span>
          <span className="admin-user-country">{countryFlag(country)} {country || "—"}</span>
          <span className={`admin-subscription-badge ${active ? "active" : ""}`}>{active ? "Подписка активна" : "Без подписки"}</span>
          <ChevronDown size={18} />
        </button>
        {expanded === user.id && <div className="admin-user-details">
          <dl><div><dt>Telegram ID</dt><dd>{user.telegramId}</dd></div><div><dt>Telegram</dt><dd>{user.username ? `@${user.username}` : "—"}</dd></div><div><dt>Логин</dt><dd>{user.login || "—"}</dd></div><div><dt>Роль</dt><dd>{user.role}</dd></div><div><dt>IP регистрации</dt><dd>{user.registration?.ip || "—"}</dd></div><div><dt>Последний IP</dt><dd>{user.lastLogin?.ip || "—"}</dd></div><div><dt>Регистрация</dt><dd>{user.registration?.at ? new Date(user.registration.at).toLocaleString("ru-RU") : "—"}</dd></div><div><dt>Последний вход</dt><dd>{user.lastLogin?.at ? new Date(user.lastLogin.at).toLocaleString("ru-RU") : "—"}</dd></div><div><dt>Входов</dt><dd>{user.loginCount || 0}</dd></div><div><dt>Подписка до</dt><dd>{active && user.subscription?.expiresAt ? new Date(user.subscription.expiresAt).toLocaleDateString("ru-RU") : "—"}</dd></div></dl>
          <div className="admin-user-agent"><strong>User Agent</strong><span>{shortAgent(user.lastLogin?.userAgent)}</span><code>{user.lastLogin?.userAgent || "—"}</code></div>
          <button className={active ? "admin-disable-sub" : "admin-enable-sub"} onClick={() => changeSubscription(user, !active)}>{active ? "Отключить подписку" : "Активировать на 30 дней"}</button>
        </div>}
      </article>;
    })}{!visible.length && <div className="admin-catalog-empty"><Users size={28} /><h3>Пользователи не найдены</h3></div>}</div>
  </section>;
}
